import React from 'react';
import Card, { CardShadow, CardStyles } from '@app/common/components/cards/Card';
import Button from '@app/common/components/Button';
import {
    Subscription,
    useCancelSubscription,
    useGetSubscription,
    useResumeSubscription,
    useSetSubscriptionPlan
} from '@app/hooks/query/useSubscriptionData';
import { useSelector } from 'react-redux';
import { getAgency, getUserInfo } from '@app/selectors/user';
import { Spinner, SpinnerSizes } from '@app/common/design-system/spinner';
import { formatDateStandard, getAgencyTimezoneFromUser } from '@app/utils/dateUtils';
import { Tag, TagBorder, TagSize } from '@app/common/components/tag/Tag';
import { SubscriptionBillingPeriod, SubscriptionStatus, subscriptionStatus } from '@app/config';
import Icon, { Icons, IconSize } from '@app/common/components/Icon';
import { isBusinessUserAccount } from '@app/utils/userUtils';
import { confirmAlert } from 'react-confirm-alert';
import CancelSubscriptionInfoModal from '@app/components/settings/subscription/modals/CancelSubscriptionInfoModal';
import { getPlans } from '@app/selectors/authorization';
import { TariffPlan } from '@app/types/TariffPlan';
import PlanInclusions from '@app/components/settings/subscription/PlanInclusions/PlanInclusions';

import styles from './CurrentPlan.module.scss';
import cx from 'classnames';
import ModalChangeSubscriptionPlanV2 from '@app/components/settings/subscription/modals/ModalChangeSubscriptionPlanV2/ModalChangeSubscriptionPlanV2';
import { useAccountProvider } from '@app/components/providers/AccountProvider';
import useToast from '@app/hooks/useToast';
import { getErrorMessageForToast } from '@app/utils/errorUtils';
import { ToastTypes } from '@app/common/components/Toast';
import { getDefaultPaymentMethod } from '@app/selectors/account';
import {
    getPlanStatusTagColor,
    getPlanStatusTagText,
    getBillingPeriodText,
    getChangeSubscriptionDetailsText
} from '@app/components/settings/subscription/modals/ModalChangeSubscriptionPlanV2/utils/planUtils';

const CurrentPlan = () => {
    const agency = useSelector(getAgency);
    const loggedInUser = useSelector(getUserInfo);
    const plans: TariffPlan[] = useSelector(getPlans);
    const defaultPaymentMethod = useSelector(getDefaultPaymentMethod);
    const accountType = useAccountProvider();

    const { addNewToast } = useToast();

    const [isOpenFeedbackModal, setIsOpenFeedbackModal] = React.useState(false);
    const [isOpenChangePlanModal, setIsOpenChangePlanModal] = React.useState(false);

    const { isLoading, isSuccess, data: subscription } = useGetSubscription(agency.id, undefined, undefined);
    const cancelSubscriptionMutation = useCancelSubscription(
        agency.id,
        () => setIsOpenFeedbackModal(true),
        () => {
            confirmAlert({
                title: '',
                message: 'Your subscription could not be cancelled. Please contact customer support for help.',
                buttons: [
                    {
                        label: 'OK',
                        onClick: () => {
                            // Do nothing, user acknowledged the error message
                        }
                    }
                ]
            });
        }
    );

    const setSubscriptionPlanMutation = useSetSubscriptionPlan(
        agency.id,
        () => {
            setIsOpenChangePlanModal(false);
        },
        () => {
            addNewToast(getErrorMessageForToast('We were unable to change your subscription plan.'), ToastTypes.ERROR);
        }
    );
    const useResumeSubscriptionMutation = useResumeSubscription(agency.id, undefined, undefined);

    function cancelSubscription() {
        let message;

        if (subscription?.status === SubscriptionStatus.ACTIVE) {
            if (isBusinessUserAccount(loggedInUser.accountType)) {
                message =
                    'Cancelling your subscription will take effect at the end of your subscription period. You will be able to continue for the rest of your subscription period. When your subscription period ends, you will not be able to create new documents, but you will still be able to access all of your existing documents. Are you sure you want to continue?';
            } else {
                message =
                    'Cancelling your subscription will take effect at the end of your subscription period. You will be able to continue for the rest of your subscription period. When your subscription period ends, you will not be able to create new leases, but you will still be able to access all of your existing agreements. Are you sure you want to continue?';
            }
        } else {
            if (isBusinessUserAccount(loggedInUser.accountType)) {
                message =
                    'Cancelling your subscription trial will take effect immediately and you will not be able to create any more new documents. You will still be able to access all of your existing documents. Are you sure you want to continue?';
            } else {
                message =
                    'Cancelling your subscription trial will take effect immediately and you will not be able to create any more new agreements. You will still be able to access all of your existing agreements. Are you sure you want to continue?';
            }
        }

        confirmAlert({
            title: '',
            message: message,
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => {
                        cancelSubscriptionMutation.mutate();
                    }
                },
                {
                    label: 'No',
                    onClick: () => {
                        // Do nothing, user cancelled the action
                    }
                }
            ]
        });
    }

    function setSubscriptionPlan(stripeId: string) {
        const params = { stripeId };
        setSubscriptionPlanMutation.mutate(params);
    }

    function resumeSubscription() {
        useResumeSubscriptionMutation.mutate();
    }

    function reActivateSubscription(stripeId: string) {
        confirmAlert({
            title: '',
            message:
                'Reactivating your subscription will continue your subscription. Are you sure you want to reactivate your subscription?',
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => {
                        setSubscriptionPlan(stripeId);
                    }
                },
                {
                    label: 'No',
                    onClick: () => {
                        // Do nothing, user cancelled the action
                    }
                }
            ]
        });
    }

    function openChangePlanModal() {
        if (defaultPaymentMethod) {
            setIsOpenChangePlanModal(true);
        } else {
            confirmAlert({
                title: '',
                message: 'Please add a payment method before changing your subscription plan.',
                buttons: [
                    {
                        label: 'OK',
                        onClick: () => {
                            // Do nothing, user acknowledged the error message
                        }
                    }
                ]
            });
        }
    }

    function handleChangeSubscriptionDetailsClick() {
        if (
            !subscription ||
            !subscription.status ||
            [SubscriptionStatus.PAST_DUE, SubscriptionStatus.UNPAID].includes(subscription.status)
        ) {
            return;
        }

        if (subscription.status === SubscriptionStatus.PAUSED) {
            if (!defaultPaymentMethod) {
                confirmAlert({
                    title: '',
                    message: 'Please add a payment method before resuming your subscription.',
                    buttons: [
                        {
                            label: 'OK',
                            onClick: () => {
                                // Do nothing, user acknowledged the error message
                            }
                        }
                    ]
                });
            } else {
                resumeSubscription();
            }
            return;
        }

        if (subscription.active && subscription.status === SubscriptionStatus.CANCELLING) {
            reActivateSubscription(subscription.stripeId);
        } else {
            openChangePlanModal();
        }
    }

    const changeSubscriptionDetailsText = getChangeSubscriptionDetailsText(subscription);

    return (
        <Card style={CardStyles.SQUARE} shadow={CardShadow.SMALL} className={styles.currentPlanCard}>
            <div className={styles.header}>
                <h2 className={styles.title}>Current Plan</h2>
                {subscription?.status && (
                    <Tag
                        text={getPlanStatusTagText(subscription.status)}
                        textClassName={styles.statusText}
                        color={getPlanStatusTagColor(subscription.status)}
                        border={TagBorder.None}
                        size={TagSize.X_Small}
                        startIcon={<div className={styles.statusIcon} />}
                    />
                )}
            </div>
            {isLoading && <Spinner size={SpinnerSizes.MEDIUM} />}
            {isSuccess && subscription && (
                <>
                    <div className={styles.titleSection}>
                        <Icon icon={Icons.BLUE_FLK_LOGO} size={IconSize.LARGE} className={styles.logo} />
                        <h3 className={styles.planTitle}>{subscription.title}</h3>
                    </div>
                    <div className={styles.priceSection}>
                        <div className={styles.priceGrid}>
                            <p className={cx(styles.price, styles.largeText)}>${subscription.monthlyPrice}</p>
                            <p className={cx(styles.currency, styles.blackText)}>
                                {subscription.currency}
                                <span className={styles.smallText}>/mth</span>
                            </p>
                            <p className={cx(styles.taxDescription, styles.smallText)}>
                                ({subscription.taxDescription})
                            </p>
                        </div>
                        <p className={styles.subTitle}>
                            {subscription.billingPeriod === SubscriptionBillingPeriod.YEARLY
                                ? `Total ${subscription.price} ${subscription.currency} - `
                                : ''}
                            Billed {getBillingPeriodText(subscription.billingPeriod)}
                        </p>
                    </div>
                    <div className={styles.section}>
                        <p className={styles.label}>
                            Plan started on{' '}
                            {formatDateStandard(subscription.created, getAgencyTimezoneFromUser(loggedInUser))}
                        </p>
                        <p className={styles.label}>
                            Renews on{' '}
                            {formatDateStandard(subscription.renewDate, getAgencyTimezoneFromUser(loggedInUser))}
                        </p>
                    </div>
                    <PlanInclusions
                        constraintText={subscription.constraintText}
                        documentConstraintText={subscription.documentConstraintText}
                        accountType={accountType}
                        className={styles.section}
                    />
                    {changeSubscriptionDetailsText && (
                        <Button
                            className={styles.changeSubscriptionDetailsButton}
                            primary
                            type="button"
                            height={32}
                            onClick={handleChangeSubscriptionDetailsClick}
                        >
                            {changeSubscriptionDetailsText}
                        </Button>
                    )}
                    {subscription.active && subscription.status === SubscriptionStatus.ACTIVE && (
                        <Button
                            className={styles.cancelSubscriptionButton}
                            error
                            type="button"
                            height={32}
                            onClick={cancelSubscription}
                        >
                            Cancel Subscription
                        </Button>
                    )}
                </>
            )}
            {isOpenFeedbackModal && (
                <CancelSubscriptionInfoModal
                    isOpen={isOpenFeedbackModal}
                    agencyId={agency.id}
                    closeModal={() => setIsOpenFeedbackModal(false)}
                />
            )}
            {subscription && isOpenChangePlanModal && (
                <ModalChangeSubscriptionPlanV2
                    isOpen={isOpenChangePlanModal}
                    plans={plans}
                    accountType={accountType}
                    currentPlan={subscription}
                    closeModal={() => setIsOpenChangePlanModal(false)}
                    isSaving={setSubscriptionPlanMutation.isLoading}
                    onPlanChange={setSubscriptionPlan}
                />
            )}
        </Card>
    );
};

export default CurrentPlan;
